import { useCallback, useEffect, useRef, useState } from 'react';
import { paConfigurationCatalogResponseSchema, type PaConfigurationCatalogResponse, type PaToolPermission } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/utils';
import { applyToolPermissionChanges } from '@/components/mcp/AgentToolPermissionsEditor';
import { PERSONAL_ASSISTANT_UPDATED_EVENT } from '@/lib/personal-assistant-events';

export function usePaConfigurationDraft(open: boolean, assistantId?: string) {
  const [catalog, setCatalog] = useState<PaConfigurationCatalogResponse | null>(null);
  const [permissions, setPermissions] = useState<PaToolPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const createRequestId = useRef('');

  const load = useCallback(async (resetDraft = true) => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.get<unknown>(assistantId
        ? `/personal-assistant/agents/${assistantId}/configuration`
        : '/personal-assistant/configuration-catalog');
      const data = paConfigurationCatalogResponseSchema.parse(result.data);
      if (current !== generation.current) return;
      setCatalog(data);
      if (resetDraft) setPermissions(data.permissions);
    } catch (cause) {
      if (current === generation.current) setError(getApiErrorMessage(cause) || 'Impossibile caricare la configurazione dei connettori');
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => {
    setCatalog(null);
    setPermissions([]);
    createRequestId.current = crypto.randomUUID();
    if (open) void load();
    return () => { generation.current += 1; };
  }, [open, load]);

  useEffect(() => {
    if (!open || !assistantId) return;
    const updated = (event: WindowEventMap[typeof PERSONAL_ASSISTANT_UPDATED_EVENT]) => {
      if (!event.detail.assistantId || event.detail.assistantId === assistantId) void load(false);
    };
    window.addEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, updated);
    return () => window.removeEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, updated);
  }, [open, assistantId, load]);

  const change = (changes: PaToolPermission[]) => setPermissions(previous => applyToolPermissionChanges(previous, changes));
  const original = new Map(catalog?.permissions.map(permission => [JSON.stringify([permission.server_id, permission.tool_name]), permission]));
  const dirty = permissions.length !== original.size || permissions.some(permission => {
    const saved = original.get(JSON.stringify([permission.server_id, permission.tool_name]));
    return !saved || saved.enabled !== permission.enabled || saved.bulk_allowed !== permission.bulk_allowed;
  });

  return { catalog, permissions, change, dirty, loading, error, reload: () => load(catalog === null), createRequestId };
}